// Same mark as Returns for a super admin, subtitled "ADMINISTRATION" instead
// of "Super admin": the two apps must not be mistaken for each other.
import { adminPanelHref } from "@/lib/admin-panel-url";
import logoAdministration from "@/assets/logoWithName-white-color-administration.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@babackapp/ui/components/sidebar";
import {
  ArrowLeft,
  ExternalLink,
  Fingerprint,
  LayoutDashboard,
  type LucideIcon,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Store,
  UserCog,
} from "lucide-react";
import { Link, useLocation, useMatch } from "react-router-dom";
import { AdminModuleFilter } from "./admin-module-filter";
import { type AdminModule, useAdminModules } from "./admin-preferences";
import { AdminShopSelector } from "./admin-shop-selector";

type AdminNavItem = {
  /** Relative to /admin. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Hidden when its module is filtered out; none = always shown. */
  module?: AdminModule;
};

type AdminNavSection = {
  label: string;
  items: AdminNavItem[];
};

/**
 * Global (shop-independent) sections of the super admin. Shop-scoped pages
 * live under /admin/shops/:shopId, listed by shopNavigation().
 */
const ADMIN_NAVIGATION: AdminNavSection[] = [
  {
    label: "Vue d'ensemble",
    items: [
      { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "shops", label: "Boutiques", icon: Store },
    ],
  },
  {
    label: "Returns",
    items: [
      {
        to: "customer-portal",
        label: "Portail client",
        icon: ShieldCheck,
        module: "returns",
      },
      {
        to: "product-recommendations",
        label: "Reco produit",
        icon: Sparkles,
        module: "returns",
      },
    ],
  },
  {
    label: "Accès",
    items: [
      {
        to: "super-admins",
        label: "Super admins",
        icon: UserCog,
        module: "global",
      },
    ],
  },
];

const TRACK_URL = import.meta.env.VITE_APP_TRACK_URL?.replace(/\/$/, "");
const TRACK_ADMIN_URL = TRACK_URL ? `${TRACK_URL}/admin` : null;

type ShopNavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Hidden when its module is filtered out; none = always shown. */
  module?: AdminModule;
  /** Another app, in a new tab: the admin stays where it is. */
  isExternal?: boolean;
};

/**
 * Pages of the selected shop. Every page shown here stays in the admin; the
 * Returns and Tracking apps are explicit exits, in a new tab.
 */
function shopNavigation(shopId: string): ShopNavItem[] {
  return [
    { href: `/admin/shops/${shopId}`, label: "Fiche", icon: Store },
    {
      href: `/admin/shops/${shopId}/configuration`,
      label: "Configuration",
      module: "global",
      icon: SlidersHorizontal,
    },
    {
      // Journal, alertes and access log moved to the admin panel.
      href: adminPanelHref(`/shared/shops/${shopId}`),
      label: "Ouvrir dans l'admin panel",
      module: "global",
      icon: ExternalLink,
      isExternal: true,
    },
    {
      href: `/admin/shops/${shopId}/product-recommendations`,
      label: "Reco produit",
      module: "returns",
      icon: Sparkles,
    },
    {
      href: `/admin/shops/${shopId}/pii-lookup`,
      label: "Données personnelles",
      module: "global",
      icon: Fingerprint,
    },
    {
      href: `/shops/${shopId}`,
      label: "Ouvrir dans Returns",
      module: "returns",
      icon: ExternalLink,
      isExternal: true,
    },
    ...(TRACK_URL
      ? [
          {
            href: `${TRACK_URL}/shops/${shopId}`,
            label: "Ouvrir dans Tracking",
            module: "tracking" as const,
            icon: ExternalLink,
            isExternal: true,
          },
        ]
      : []),
  ];
}

function ShopNavLink({
  item: { href, label, icon: Icon, isExternal },
  isActive,
}: {
  item: ShopNavItem;
  isActive: boolean;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={label}>
        {isExternal ? (
          <a href={href} target="_blank" rel="noreferrer">
            <Icon />
            <span>{label}</span>
          </a>
        ) : (
          <Link to={href}>
            <Icon />
            <span>{label}</span>
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AdminSidebar() {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const shopId = useMatch("/admin/shops/:shopId/*")?.params.shopId ?? null;
  const [modules, setModules] = useAdminModules();
  const isShown = ({ module }: { module?: AdminModule }) =>
    !module || modules.includes(module);
  const sections = ADMIN_NAVIGATION.map((section) => ({
    ...section,
    items: section.items.filter(isShown),
  })).filter(({ items }) => items.length > 0);
  const shopItems = shopId ? shopNavigation(shopId).filter(isShown) : [];
  const shopExits = shopItems.filter(({ isExternal }) => isExternal);

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader className={state === "collapsed" ? "items-center" : ""}>
        <Link to="/admin" className="py-2">
          <img
            className="h-8 w-auto"
            src={logoAdministration}
            alt="Baback Administration"
          />
        </Link>
        <AdminShopSelector shopId={shopId} />
        <AdminModuleFilter modules={modules} onChange={setModules} />
      </SidebarHeader>
      <SidebarContent>
        {shopId && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/80">
              Boutique
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {shopItems
                  .filter(({ isExternal }) => !isExternal)
                  .map((item) => (
                    <ShopNavLink
                      key={item.href}
                      item={item}
                      isActive={pathname === item.href}
                    />
                  ))}
              </SidebarMenu>
              {/* Exits to the other apps, apart from the admin pages. */}
              {shopExits.length > 0 && (
                <SidebarMenu className="mt-2 border-t border-sidebar-border pt-2">
                  {shopExits.map((item) => (
                    <ShopNavLink key={item.href} item={item} isActive={false} />
                  ))}
                </SidebarMenu>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {/* A shop selected: its pages only, the global sections come back
            with "Tous les shops" in the selector. */}
        {!shopId &&
          sections.map((section) => (
            <SidebarGroup key={section.label}>
              <SidebarGroupLabel className="text-sidebar-foreground/80">
                {section.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map(({ to, label, icon: Icon }) => {
                    const href = `/admin/${to}`;
                    return (
                      <SidebarMenuItem key={to}>
                        <SidebarMenuButton
                          asChild
                          isActive={pathname === href}
                          tooltip={label}
                        >
                          <Link to={href}>
                            <Icon />
                            <span>{label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        {!shopId && TRACK_ADMIN_URL && modules.includes("tracking") && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/80">
              Tracking
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Admin Tracking">
                    <a href={TRACK_ADMIN_URL} target="_blank" rel="noreferrer">
                      <ExternalLink />
                      <span>Admin Tracking</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Retour à Returns">
              {/* "/" lands on the super admin default shop. */}
              <Link to="/">
                <ArrowLeft />
                <span>Retour à Returns</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
